import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { COOKIE_BANNER } from './cookieBannerI18n';

const STORAGE_KEY = 'jhouse_cookie_consent';

export const CookieConsentBanner = () => {
  const { t: siteT } = useLanguage();
  const [helpLang, setHelpLang] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onLang = (e) => setHelpLang(e.detail || null);
    window.addEventListener('jhouse-help-lang', onLang);
    return () => window.removeEventListener('jhouse-help-lang', onLang);
  }, []);

  const hb = helpLang && COOKIE_BANNER[helpLang];
  const t = (key) => {
    const k = key.replace('cookieConsent.', '');
    return hb && hb[k] ? hb[k] : siteT(key);
  };
  const rtl = helpLang === 'ar' || helpLang === 'he';
  const [showPolicy, setShowPolicy] = useState(false);

  useEffect(() => {
    if (window.__IS_APPLE_MOBILE__) return;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      const timer = setTimeout(() => setShow(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleChoice = (choice) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      choice,
      timestamp: new Date().toISOString(),
    }));
    setShow(false);
  };

  return (
    <>
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 pointer-events-none"
            data-testid="cookie-consent-banner"
          >
            <div dir={hb ? (rtl ? 'rtl' : 'ltr') : undefined} className="pointer-events-auto max-w-5xl mx-auto bg-navy-100/95 backdrop-blur-md border border-gold/30 rounded-2xl shadow-2xl p-5 md:p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gold/15 flex items-center justify-center">
                  <Cookie className="w-6 h-6 text-gold" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold mb-1" data-testid="cookie-banner-title">
                    {t('cookieConsent.title')}
                  </h3>
                  <p className="text-slate-300 text-sm leading-relaxed">
                    {t('cookieConsent.message')}{' '}
                    <button
                      onClick={() => setShowPolicy(true)}
                      className="text-gold underline hover:text-amber-400"
                      data-testid="cookie-banner-policy-link"
                    >
                      {t('cookieConsent.learnMore')}
                    </button>
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <button
                      onClick={() => handleChoice('accepted')}
                      className="px-5 py-2 bg-gradient-to-r from-gold to-amber-600 text-navy-200 font-bold rounded-lg hover:from-amber-500 hover:to-gold transition-all text-sm"
                      data-testid="cookie-accept-btn"
                    >
                      {t('cookieConsent.acceptAll')}
                    </button>
                    <button
                      onClick={() => handleChoice('necessary')}
                      className="px-5 py-2 bg-navy-200/60 text-slate-200 border border-slate-600 rounded-lg hover:bg-navy-200 transition-colors text-sm"
                      data-testid="cookie-necessary-btn"
                    >
                      {t('cookieConsent.necessaryOnly')}
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => handleChoice('necessary')}
                  className="flex-shrink-0 text-slate-400 hover:text-white p-1"
                  aria-label="Close"
                  data-testid="cookie-banner-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPolicy && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10005] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={() => setShowPolicy(false)}
            data-testid="cookie-policy-modal"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-navy-100 border border-gold/30 rounded-2xl shadow-2xl max-w-3xl w-full my-8 overflow-hidden"
            >
              <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Cookie className="w-5 h-5 text-gold" />
                  {t('cookieConsent.policyTitle')}
                </h3>
                <button
                  onClick={() => setShowPolicy(false)}
                  className="text-slate-400 hover:text-white p-1"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 max-h-[60vh] overflow-y-auto space-y-4 text-slate-300 text-sm leading-relaxed">
                <section>
                  <h4 className="text-gold font-semibold mb-2">{t('cookieConsent.section1Title')}</h4>
                  <p>{t('cookieConsent.section1Body')}</p>
                </section>
                <section>
                  <h4 className="text-gold font-semibold mb-2">{t('cookieConsent.section2Title')}</h4>
                  <p>{t('cookieConsent.section2Body')}</p>
                </section>
                <section>
                  <h4 className="text-gold font-semibold mb-2">{t('cookieConsent.section3Title')}</h4>
                  <ul className="list-disc list-inside space-y-1 text-slate-400">
                    <li>{t('cookieConsent.cookieType1')}</li>
                    <li>{t('cookieConsent.cookieType2')}</li>
                    <li>{t('cookieConsent.cookieType3')}</li>
                  </ul>
                </section>
                <section>
                  <h4 className="text-gold font-semibold mb-2">{t('cookieConsent.section4Title')}</h4>
                  <p>{t('cookieConsent.section4Body')}</p>
                </section>
                <section>
                  <h4 className="text-gold font-semibold mb-2">{t('cookieConsent.contactTitle')}</h4>
                  <p>
                    📧 j.houseofsecretary@cytanet.com.cy<br />
                    📞 +357 22 321316
                  </p>
                </section>
              </div>
              <div className="px-6 py-4 border-t border-slate-800 flex gap-3 justify-end">
                <button
                  onClick={() => { handleChoice('necessary'); setShowPolicy(false); }}
                  className="px-4 py-2 text-slate-300 hover:text-white text-sm"
                >
                  {t('cookieConsent.necessaryOnly')}
                </button>
                <button
                  onClick={() => { handleChoice('accepted'); setShowPolicy(false); }}
                  className="px-5 py-2 bg-gradient-to-r from-gold to-amber-600 text-navy-200 font-bold rounded-lg hover:from-amber-500 hover:to-gold text-sm"
                >
                  {t('cookieConsent.acceptAll')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsentBanner;
